import { useEffect, useRef, type RefObject } from 'react';
import AxiSettingsThemeSection from './AxiSettingsThemeSection';
import AxiSettingsAboutSection from './AxiSettingsAboutSection';

export type AxiSettingsPanelProps = {
  /** Whether the panel is currently open. */
  open: boolean;
  /** Called to close the panel: backdrop click, X button, ESC key. */
  onClose: () => void;
  /**
   * Ref to the trigger button (gear icon). When the panel closes, focus
   * is restored to this element. Caller wires the same ref into
   * `<AxiDarkModeToggle buttonRef={triggerRef}>`.
   */
  triggerRef: RefObject<HTMLButtonElement | null>;
};

/**
 * Right-side slide-in settings panel. Renders a backdrop and a 360px-wide
 * drawer. The drawer body hosts a single `<AxiSettingsThemeSection>` for
 * now; future sections (layout, compact) can be added without changing
 * the panel shell.
 *
 * Behavior:
 *   - Backdrop click → onClose.
 *   - X button → onClose.
 *   - ESC key → onClose.
 *   - On open, focus moves to the X button.
 *   - On close, focus returns to `triggerRef.current`.
 */
function AxiSettingsPanel({ open, onClose, triggerRef }: AxiSettingsPanelProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    // Defer focus so the panel's transform/opacity transition can start.
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
      // Restore focus to the trigger when the panel closes.
      triggerRef.current?.focus();
    };
  }, [open, onClose, triggerRef]);

  return (
    <>
      <div
        className={
          'axi-settings-panel__backdrop' + (open ? ' is-open' : '')
        }
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        id="axi-settings-panel"
        className={'axi-settings-panel' + (open ? ' is-open' : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="axi-settings-panel-title"
        aria-hidden={open ? undefined : true}
      >
        <header className="axi-settings-panel__header">
          <h2
            id="axi-settings-panel-title"
            className="axi-settings-panel__title"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            设置 / Settings
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="axi-settings-panel__close"
            aria-label="关闭"
            onClick={onClose}
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>
        <div className="axi-settings-panel__body">
          <AxiSettingsThemeSection onPick={onClose} />
          <AxiSettingsAboutSection />
        </div>
      </aside>
    </>
  );
}

export default AxiSettingsPanel;